import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Form, Input, Select, Switch, Tag, Space, Popconfirm, message, Row, Col, Statistic, Typography, Divider, Badge, Empty, Tabs } from 'antd';
import {
  UserAddOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CheckOutlined, CloseOutlined,
  ClockCircleOutlined, UserOutlined, CrownOutlined, SafetyCertificateOutlined,
  SearchOutlined, EyeOutlined, MailOutlined, GlobalOutlined
} from '@ant-design/icons';
import { listUsers, createUser, updateUser, deleteUser, setUserActive, setUserApproval } from '../services/adminService';
import { useTheme } from '../contexts/ThemeContext';

const { Option } = Select;
const { Title, Text } = Typography;

// ── pure filter predicates (operate on already-loaded user rows) ───────
function fieldMatchesSearch(value, q) {
  return !!value && value.toLowerCase().includes(q);
}

export function userMatchesSearch(user, searchText) {
  const q = (searchText || '').trim().toLowerCase();
  if (!q) {return true;}
  return fieldMatchesSearch(user.username, q)
    || fieldMatchesSearch(user.email, q)
    || fieldMatchesSearch(user.full_name, q);
}

export function userMatchesRole(user, roleFilter) {
  if (roleFilter === 'all') {return true;}
  if (roleFilter === 'admin') {return user.role === 'Admin';}
  if (roleFilter === 'user') {return user.role === 'User';}
  return false;
}

export function userMatchesStatus(user, statusFilter) {
  if (statusFilter === 'all') {return true;}
  if (statusFilter === 'active') {return !!user.is_active;}
  if (statusFilter === 'inactive') {return !user.is_active;}
  return false;
}

export function userMatchesApproval(user, approvalFilter) {
  if (approvalFilter === 'all') {return true;}
  return (user.approval_status || 'approved') === approvalFilter;
}

export function matchesUserFilters(user, { searchText, roleFilter, statusFilter, approvalFilter }) {
  return userMatchesSearch(user, searchText)
    && userMatchesRole(user, roleFilter)
    && userMatchesStatus(user, statusFilter)
    && userMatchesApproval(user, approvalFilter);
}

// ── pure presentation helpers ───────────────────────────────────────────
export function approvalTag(status) {
  if (status === 'pending') {return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;}
  if (status === 'rejected') {return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;}
  return <Tag color="green" icon={<CheckCircleOutlined />}>Approved</Tag>;
}

export function renderRoleTag(user) {
  if (user.role === 'Admin') {return <Tag icon={<CrownOutlined />} color="gold">Admin</Tag>;}
  if (user.role === 'User') {return <Tag icon={<UserOutlined />} color="green">User</Tag>;}
  return <Tag>{user.role || 'User'}</Tag>;
}

// Modal copy depends only on whether we're editing vs. creating.
export function userModalCopy(editingUser) {
  return editingUser ? { title: 'Edit User', okText: 'Save' } : { title: 'Create User', okText: 'Create' };
}

function userListCardStyle(isSelected, themeConfig) {
  return {
    cursor: 'pointer',
    border: isSelected ? `2px solid ${themeConfig.primary}` : `1px solid ${themeConfig.border}`,
    borderRadius: 8,
    background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#ffffff',
    boxShadow: isSelected ? '0 4px 10px rgba(0,0,0,0.14)' : '0 1px 3px rgba(0,0,0,0.06)',
    transition: 'all 0.2s',
    position: 'relative',
    overflow: 'visible',
  };
}

// Verified users show active/inactive; an unverified email overrides
// that with a single "Unverified" tag regardless of active state.
function UserVerificationTag({ user }) {
  if (!user.email_verified) {
    return <Tag icon={<MailOutlined />} color="orange" style={{ margin: 0, flexShrink: 0 }}>Unverified</Tag>;
  }
  return (
    <Tag
      icon={user.is_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
      color={user.is_active ? 'green' : 'red'}
      style={{ margin: 0, flexShrink: 0 }}
    >
      {user.is_active ? 'Active' : 'Inactive'}
    </Tag>
  );
}

// ── one row of the user list (presentational — no permission logic) ────
function UserListCard({ user, isSelected, themeConfig, onSelect, onApprove, onReject, onEdit, onToggleActive, onDelete }) {
  const isPending = user.approval_status === 'pending';
  return (
    <Card
      hoverable
      onClick={() => onSelect(user)}
      style={userListCardStyle(isSelected, themeConfig)}
      styles={{ body: { padding: '8px 12px' } }}
    >
      {/* Left accent bar for selected user */}
      {isSelected && (
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: themeConfig.primary,
          borderTopLeftRadius: 6,
          borderBottomLeftRadius: 6,
        }} />
      )}

      <Row align="middle" gutter={8} wrap={false} style={{ width: '100%' }}>
        {/* Main info */}
        <Col flex="auto" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', overflow: 'hidden' }}>
            <Text strong style={{ fontSize: 14, flexShrink: 0 }}>{user.username}</Text>
            <span style={{ flexShrink: 0 }}>{renderRoleTag(user)}</span>
            <UserVerificationTag user={user} />
            <span style={{ flexShrink: 0 }}>{approvalTag(user.approval_status)}</span>
            <Text
              type="secondary"
              ellipsis={{ tooltip: user.email }}
              style={{ fontSize: 12, minWidth: 0, flex: '1 1 auto' }}
            >
              <MailOutlined style={{ marginRight: 4 }} />{user.email}
              {user.full_name ? ` · ${user.full_name}` : ''}
            </Text>
          </div>
        </Col>

        {/* Action buttons */}
        <Col flex="none">
          <Space size={0} onClick={(e) => e.stopPropagation()}>
            {isPending && (
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined style={{ color: '#52c41a' }} />}
                  title="Approve"
                  onClick={(e) => { e.stopPropagation(); onApprove(user.id); }}
                />
                <Popconfirm
                  title="Reject this user?"
                  onConfirm={(e) => { e?.stopPropagation?.(); onReject(user.id); }}
                >
                  <Button type="text" size="small" danger icon={<CloseOutlined />} title="Reject" />
                </Popconfirm>
              </>
            )}
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              title="Edit User"
              onClick={(e) => { e.stopPropagation(); onEdit(user); }}
            />
            <Popconfirm
              title={`${user.is_active ? 'Deactivate' : 'Activate'} this user?`}
              onConfirm={(e) => { e?.stopPropagation?.(); onToggleActive(user.id, !user.is_active); }}
            >
              <Button
                type="text"
                size="small"
                icon={user.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                title={user.is_active ? 'Deactivate' : 'Activate'}
              />
            </Popconfirm>
            <Popconfirm
              title="Delete User"
              description="Are you sure? This action cannot be undone."
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={(e) => { e?.stopPropagation?.(); onDelete(user.id); }}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete" />
            </Popconfirm>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}

// ── right-column "Preview" card (presentational — no permission logic) ──
function UserPreviewPanel({ selectedUser, themeConfig, activeTab, onTabChange, onApprove, onReject }) {
  return (
    <Card
      title={
        <Space>
          <EyeOutlined style={{ color: themeConfig.primary }} />
          <span style={{ fontWeight: 600 }}>Preview</span>
        </Space>
      }
      style={{
        border: `1px solid ${themeConfig.border}`,
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        height: 'calc(100vh - 270px)',
        overflow: 'visible',
      }}
      styles={{ body: { overflowY: 'auto', height: 'calc(100% - 57px)', padding: 0 } }}
    >
      {!selectedUser ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Empty
            description={
              <Space orientation="vertical" size={4}>
                <Text type="secondary" style={{ fontSize: 15 }}>Select a user to view details</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>Click on any user from the list</Text>
              </Space>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: 40 }}
          />
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          style={{ margin: 16 }}
          items={[
            {
              key: 'profile',
              label: <span><UserOutlined /> Profile</span>,
              children: (
                <div style={{ padding: '12px 4px' }}>
                  <div style={{ marginBottom: 16 }}>
                    <Tag color={themeConfig.primary} variant="solid" style={{ padding: '6px 14px', fontSize: 14, borderRadius: 4 }}>
                      <Space>
                        <UserOutlined />
                        {selectedUser.username}
                      </Space>
                    </Tag>
                  </div>

                  <Space orientation="vertical" style={{ width: '100%' }} size={20}>
                    <div>
                      <Title level={5} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <UserOutlined style={{ color: themeConfig.primary }} />
                        User Information
                      </Title>
                      <Space orientation="vertical" style={{ width: '100%' }} size={8}>
                        <Row><Col span={10}><Text strong>Username:</Text></Col><Col span={14}><Text>{selectedUser.username}</Text></Col></Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row><Col span={10}><Text strong>Email:</Text></Col><Col span={14}><Text copyable>{selectedUser.email}</Text></Col></Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row><Col span={10}><Text strong>Full Name:</Text></Col><Col span={14}><Text>{selectedUser.full_name || <Text type="secondary">Not set</Text>}</Text></Col></Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row>
                          <Col span={10}><Text strong>Timezone:</Text></Col>
                          <Col span={14}>
                            <Space>
                              <GlobalOutlined style={{ color: themeConfig.primary }} />
                              <Text>{selectedUser.timezone || 'UTC'}</Text>
                            </Space>
                          </Col>
                        </Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row><Col span={10}><Text strong>Role:</Text></Col><Col span={14}>{renderRoleTag(selectedUser)}</Col></Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row>
                          <Col span={10}><Text strong>Status:</Text></Col>
                          <Col span={14}>
                            {selectedUser.is_active
                              ? <Tag icon={<CheckCircleOutlined />} color="green">Active</Tag>
                              : <Tag icon={<CloseCircleOutlined />} color="red">Inactive</Tag>}
                          </Col>
                        </Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row>
                          <Col span={10}><Text strong>Verified:</Text></Col>
                          <Col span={14}>
                            {selectedUser.email_verified
                              ? <Tag icon={<SafetyCertificateOutlined />} color="blue">Verified</Tag>
                              : <Tag icon={<MailOutlined />} color="orange">Unverified</Tag>}
                          </Col>
                        </Row>
                        <Divider style={{ margin: '4px 0' }} />
                        <Row><Col span={10}><Text strong>Approval:</Text></Col><Col span={14}>{approvalTag(selectedUser.approval_status)}</Col></Row>
                        {selectedUser.created_at && (
                          <>
                            <Divider style={{ margin: '4px 0' }} />
                            <Row>
                              <Col span={10}><Text strong>Created:</Text></Col>
                              <Col span={14}><Text style={{ fontSize: 13 }}>{new Date(selectedUser.created_at).toLocaleString()}</Text></Col>
                            </Row>
                          </>
                        )}
                        {selectedUser.updated_at && (
                          <>
                            <Divider style={{ margin: '4px 0' }} />
                            <Row>
                              <Col span={10}><Text strong>Last Updated:</Text></Col>
                              <Col span={14}><Text style={{ fontSize: 13 }}>{new Date(selectedUser.updated_at).toLocaleString()}</Text></Col>
                            </Row>
                          </>
                        )}
                      </Space>
                    </div>

                    {selectedUser.approval_status === 'pending' && (
                      <Space>
                        <Button type="primary" icon={<CheckOutlined />} onClick={() => onApprove(selectedUser.id)}>Approve</Button>
                        <Popconfirm title="Reject this user?" onConfirm={() => onReject(selectedUser.id)}>
                          <Button danger icon={<CloseOutlined />}>Reject</Button>
                        </Popconfirm>
                      </Space>
                    )}
                  </Space>
                </div>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

// ── create/edit user modal (presentational — no permission logic) ──────
function UserFormModal({ open, editingUser, form, onSubmit, onCancel }) {
  const { title, okText } = userModalCopy(editingUser);
  return (
    <Modal title={title} open={open} onOk={onSubmit} onCancel={onCancel} okText={okText}>
      <Form form={form} layout="vertical">
        <Form.Item name="email" label="Email" rules={[{ required: !editingUser, type: 'email', message: 'Valid email required' }]}>
          <Input disabled={!!editingUser} />
        </Form.Item>
        <Form.Item name="username" label="Username" rules={[{ required: !editingUser, message: 'Username required' }]}>
          <Input disabled={!!editingUser} />
        </Form.Item>
        {!editingUser && (
          <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Password required' }]}>
            <Input.Password />
          </Form.Item>
        )}
        <Form.Item name="full_name" label="Full Name">
          <Input />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select options={[{ value: 'Admin', label: 'Admin' }, { value: 'User', label: 'User' }]} />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const { themeConfig } = useTheme();

  // Filter / search state
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('profile');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setActiveTab('profile');
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'User', is_active: true });
    setModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await updateUser(editingUser.id, values);
        message.success('User updated');
      } else {
        await createUser(values);
        message.success('User created');
      }
      setModalVisible(false);
      loadUsers();
    } catch (err) {
      if (err.message) {message.error(err.message);}
    }
  };

  const handleDelete = async (userId) => {
    try {
      await deleteUser(userId);
      message.success('User deleted');
      if (selectedUser?.id === userId) {setSelectedUser(null);}
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  // Pass the state we want rather than asking the server to flip: the row
  // already knows `user.is_active`, and a flip made double-clicks a no-op.
  const handleSetActive = async (userId, isActive) => {
    try {
      await setUserActive(userId, isActive);
      message.success(isActive ? 'User activated' : 'User deactivated');
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const handleApproval = async (userId, status) => {
    try {
      await setUserApproval(userId, status);
      message.success(status === 'approved' ? 'User approved' : 'User rejected');
      loadUsers();
    } catch (err) { message.error(err.message); }
  };

  const pendingCount = users.filter(u => u.approval_status === 'pending').length;

  // ── Filtering ────────────────────────────────────────
  const filteredUsers = useMemo(
    () => users.filter((user) => matchesUserFilters(user, { searchText, roleFilter, statusFilter, approvalFilter })),
    [users, searchText, roleFilter, statusFilter, approvalFilter],
  );

  const statCardStyle = (accent) => ({
    borderLeft: `4px solid ${accent}`,
    border: `1px solid ${themeConfig.border}`,
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#ffffff',
  });

  const cardChromeStyle = {
    border: `1px solid ${themeConfig.border}`,
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  };

  return (
    <div style={{ padding: 24, background: themeConfig.contentBg, minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title
              level={3}
              style={{
                margin: 0,
                color: themeConfig.primary,
                fontWeight: 'bold',
                letterSpacing: 1,
              }}
            >
              USER MANAGEMENT
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Manage users, roles, and approvals
            </Text>
          </Col>
        </Row>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small" style={statCardStyle(themeConfig.primary)} styles={{ body: { padding: '12px 16px' } }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Total Users</Text>}
              value={users.length}
              prefix={<UserOutlined style={{ color: themeConfig.primary, fontSize: 18 }} />}
              styles={{ content: { color: themeConfig.primary, fontSize: 24, fontWeight: 'bold' } }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#52c41a')} styles={{ body: { padding: '12px 16px' } }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Active</Text>}
              value={users.filter(u => u.is_active).length}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />}
              styles={{ content: { color: '#52c41a', fontSize: 24, fontWeight: 'bold' } }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#cf1322')} styles={{ body: { padding: '12px 16px' } }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Admins</Text>}
              value={users.filter(u => u.role === 'Admin').length}
              prefix={<CrownOutlined style={{ color: '#cf1322', fontSize: 18 }} />}
              styles={{ content: { color: '#cf1322', fontSize: 24, fontWeight: 'bold' } }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small" style={statCardStyle('#1890ff')} styles={{ body: { padding: '12px 16px' } }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Verified</Text>}
              value={users.filter(u => u.email_verified).length}
              prefix={<SafetyCertificateOutlined style={{ color: '#1890ff', fontSize: 18 }} />}
              styles={{ content: { color: '#1890ff', fontSize: 24, fontWeight: 'bold' } }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small" style={statCardStyle(pendingCount > 0 ? '#fa8c16' : '#999')} styles={{ body: { padding: '12px 16px' } }}>
            <Statistic
              title={<Text style={{ fontSize: 12 }}>Pending Approval</Text>}
              value={pendingCount}
              prefix={<ClockCircleOutlined style={{ color: pendingCount > 0 ? '#fa8c16' : '#999', fontSize: 18 }} />}
              styles={{ content: { color: pendingCount > 0 ? '#fa8c16' : '#999', fontSize: 24, fontWeight: 'bold' } }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Left Column - User List (65%) */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UserOutlined style={{ color: themeConfig.primary }} />
                <span style={{ fontWeight: 600 }}>List</span>
                <Badge
                  count={filteredUsers.length}
                  showZero
                  style={{ backgroundColor: themeConfig.primary }}
                />
              </Space>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadUsers}>Refresh</Button>
                <Button type="primary" icon={<UserAddOutlined />} onClick={handleCreate}>Create User</Button>
              </Space>
            }
            loading={loading}
            style={{
              ...cardChromeStyle,
              height: 'calc(100vh - 270px)',
              overflow: 'visible',
            }}
            styles={{ body: { padding: 0, overflowY: 'auto', overflowX: 'hidden', height: 'calc(100% - 57px)' } }}
          >
            {/* Filter Controls - Single Line Layout */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${themeConfig.border}`,
              background: themeConfig.mode === 'dark' ? themeConfig.backgroundSecondary : '#fafafa',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <Input
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Search by username, email, or full name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col style={{ minWidth: 130 }}>
                  <Select value={roleFilter} onChange={setRoleFilter} style={{ width: '100%' }} placeholder="Role">
                    <Option value="all">All Roles</Option>
                    <Option value="admin">Admin</Option>
                    <Option value="user">User</Option>
                  </Select>
                </Col>
                <Col style={{ minWidth: 130 }}>
                  <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }} placeholder="Status">
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Col>
                <Col style={{ minWidth: 140 }}>
                  <Select value={approvalFilter} onChange={setApprovalFilter} style={{ width: '100%' }} placeholder="Approval">
                    <Option value="all">All Approvals</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="approved">Approved</Option>
                    <Option value="rejected">Rejected</Option>
                  </Select>
                </Col>
              </Row>
            </div>

            {/* User List Cards - compact rows */}
            <div style={{ padding: 10 }}>
              {filteredUsers.length === 0 ? (
                <Empty description="No users found" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '40px 0' }} />
              ) : (
                <Space orientation="vertical" style={{ width: '100%' }} size={6}>
                  {filteredUsers.map(user => (
                    <UserListCard
                      key={user.id}
                      user={user}
                      isSelected={selectedUser?.id === user.id}
                      themeConfig={themeConfig}
                      onSelect={handleSelectUser}
                      onApprove={(id) => handleApproval(id, 'approved')}
                      onReject={(id) => handleApproval(id, 'rejected')}
                      onEdit={handleEdit}
                      onToggleActive={handleSetActive}
                      onDelete={handleDelete}
                    />
                  ))}
                </Space>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Preview (35%) - always visible */}
        <Col xs={24} lg={8}>
          <UserPreviewPanel
            selectedUser={selectedUser}
            themeConfig={themeConfig}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onApprove={(id) => handleApproval(id, 'approved')}
            onReject={(id) => handleApproval(id, 'rejected')}
          />
        </Col>
      </Row>

      <UserFormModal
        open={modalVisible}
        editingUser={editingUser}
        form={form}
        onSubmit={handleSubmit}
        onCancel={() => setModalVisible(false)}
      />
    </div>
  );
}

export default UserManagement;
